import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getSafeRedirectPath, setAuth } from '../auth';
import { apiRequest } from '../utils/api';
import FormFeedback, { FieldError, inputErrorClass } from './Common/FormFeedback';
import './LoginRegister.css';

export default function LoginRegister({ mode = 'login' }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const feedbackRef = useRef(null);
  const isLogin = mode === 'login';
  const redirect = searchParams.get('redirect');
  const initialRole = searchParams.get('role') === 'employer' ? 'employer' : 'seeker';

  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [referralEpicJobsId, setReferralEpicJobsId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [verificationEmail, setVerificationEmail] = useState('');
  const shouldScrollToFeedback = useRef(false);

  function redirectUser(user) {
    navigate(getSafeRedirectPath(redirect, user), { replace: true });
  }

  function authLink(nextMode) {
    const params = new URLSearchParams();

    if (redirect) {
      params.set('redirect', redirect);
    }

    if (nextMode === 'signup' && searchParams.get('role') === 'employer') {
      params.set('role', 'employer');
    }

    const query = params.toString();

    return `/${nextMode}${query ? `?${query}` : ''}`;
  }

  function scrollToFeedback() {
    window.requestAnimationFrame(() => {
      const feedback = feedbackRef.current;

      if (!feedback) return;

      const headerOffset = 220;
      const top = feedback.getBoundingClientRect().top + window.pageYOffset - headerOffset;

      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      feedback.focus({ preventScroll: true });
    });
  }

  function queueFeedbackScroll() {
    shouldScrollToFeedback.current = true;
  }

  useEffect(() => {
    const hasFeedback = Boolean(success || error || validationErrors.length > 0);

    if (!shouldScrollToFeedback.current || !hasFeedback) return;

    shouldScrollToFeedback.current = false;
    scrollToFeedback();
  }, [success, error, validationErrors]);

  function handleSubmit(e) {
    e.preventDefault();

    setIsLoading(true);
    setError(false);
    setSuccess(false);
    setValidationErrors([]);
    setFieldErrors({});
    setVerificationEmail('');

    let formData = new FormData();
    formData.append('email', email);
    formData.append('password', password);

    if (!isLogin) {
      formData.append('name', name);
      formData.append('role', role);
      formData.append('company_name', companyName);
      formData.append('referral_epic_jobs_id', referralEpicJobsId);
      formData.append('terms_accepted', termsAccepted ? '1' : '');
    }

    apiRequest(isLogin ? '/login' : '/register', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
      },
      body: formData,
    })
      .then(data => {
        setIsLoading(false);

        if (data?.status === 'success') {
          queueFeedbackScroll();
          setSuccess(data.message);
          if (data.email_verification_required) {
            setVerificationEmail(data.user?.email || email);
          }
          if (data.access_token) {
            setAuth(data.access_token, data.user);
            redirectUser(data.user);
          }
        }
        else {
          queueFeedbackScroll();
          setError(isLogin ? 'There was some error while logging in, please try again.' : 'There was some error while creating account, please try again.');
        }
      })
      .catch(error => {
        setIsLoading(false);
        if (error.data?.status === 'validation_error') {
          queueFeedbackScroll();
          setValidationErrors(error.messages);
          setFieldErrors(error.errors);
        }
        else {
          queueFeedbackScroll();
          if (error.data?.email_verification_required) {
            setVerificationEmail(error.data.email || email);
          }
          setError(error.message);
        }
      });
  }

  function resendVerification() {
    if (!verificationEmail) return;

    setIsLoading(true);
    setError(false);
    setSuccess(false);

    const formData = new FormData();
    formData.append('email', verificationEmail);

    apiRequest('/email/verification-notification', {
      method: 'POST',
      body: formData,
    })
      .then(data => {
        setIsLoading(false);
        queueFeedbackScroll();
        setSuccess(data.message);
      })
      .catch(error => {
        setIsLoading(false);
        queueFeedbackScroll();
        setError(error.message);
      });
  }

  return (
    <div className={`login-form default-form ${isLogin ? 'auth-login-form' : 'auth-register-form'}`}>
      <div className="form-inner">
        <h3>{isLogin ? 'Login to Epic-Jobs' : 'Create an account'}</h3>

        <div ref={feedbackRef} tabIndex={-1}>
          <FormFeedback success={success} error={error} validationErrors={validationErrors} />
          {verificationEmail && (
            <div className="auth-helper-box">
              <button type="button" className="call-modal signup" onClick={resendVerification} disabled={isLoading}>
                Resend verification email
              </button>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <div className="form-group">
                <label>Account Type</label>
                <div className="auth-role-selector">
                  <button type="button" className={role === 'seeker' ? 'active' : ''} onClick={() => setRole('seeker')}>
                    Job Seeker
                  </button>
                  <button type="button" className={role === 'employer' ? 'active' : ''} onClick={() => setRole('employer')}>
                    Employer
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>{role === 'employer' ? 'Contact Person Name' : 'Full Name'}</label>
                <input type="text" name="name" className={inputErrorClass(fieldErrors, 'name')} placeholder="Your name" required value={name} onChange={(e) => setName(e.target.value)} />
                <FieldError errors={fieldErrors} name="name" />
              </div>

              {role === 'employer' && (
                <div className="form-group">
                  <label>Company Name</label>
                  <input type="text" name="company_name" className={inputErrorClass(fieldErrors, 'company_name')} placeholder="Company name" required value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                  <FieldError errors={fieldErrors} name="company_name" />
                </div>
              )}

              <div className="form-group">
                <label>Referral Epic-Jobs ID <span className="optional">(optional)</span></label>
                <input
                  type="text"
                  name="referral_epic_jobs_id"
                  className={inputErrorClass(fieldErrors, 'referral_epic_jobs_id')}
                  placeholder="Example: 101"
                  value={referralEpicJobsId}
                  onChange={(e) => setReferralEpicJobsId(e.target.value)}
                />
                <FieldError errors={fieldErrors} name="referral_epic_jobs_id" />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Email Address</label>
            <input type="email" name="email" className={inputErrorClass(fieldErrors, 'email')} placeholder="Email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <FieldError errors={fieldErrors} name="email" />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input id="password-field" type="password" name="password" className={inputErrorClass(fieldErrors, 'password')} placeholder="Password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            <FieldError errors={fieldErrors} name="password" />
          </div>

          <div className="form-group">
            <div className="field-outer">
              <div className="input-group checkboxes square">
                <input
                  id={isLogin ? 'remember' : 'terms_accepted'}
                  type="checkbox"
                  checked={isLogin ? remember : termsAccepted}
                  onChange={(e) => isLogin ? setRemember(e.target.checked) : setTermsAccepted(e.target.checked)}
                  required={!isLogin}
                />
                <label htmlFor={isLogin ? 'remember' : 'terms_accepted'} className="remember"><span className="custom-checkbox"></span> {isLogin ? 'Remember me' : 'I agree to the Epic-Jobs User Terms'}</label>
              </div>
              {isLogin && <Link className="pwd" to="/forgot-password">Forgot password?</Link>}
            </div>
            {!isLogin && <FieldError errors={fieldErrors} name="terms_accepted" />}
          </div>

          {!isLogin && role === 'employer' && (
            <div className="auth-helper-box">
              Employer accounts start with 1 trial job post. Pay-per-post and Best Value purchases will attach to this account.
            </div>
          )}

          {!isLogin && (
            <div className="auth-terms-box">
              <h4>Epic-Jobs User Terms</h4>
              <p>All users of Epic-Jobs information and services agree to the User Terms during registration.</p>
              <ul>
                <li>Job seekers may register to post their CV/resume and use Epic-Jobs for personal employment or job search activity.</li>
                <li>Job seeker CV/resume information may be distributed, with advanced permission, to job opportunities they apply for on Epic-Jobs.</li>
                <li>Employers, recruiters, and employment services may post job ads, advertise employment opportunities, search CV/resumes, and use post jobs functions for a fee to be determined based on market conditions.</li>
                <li>Users acknowledge Epic-Jobs is a copyright protected website and grant rights for republishing and distribution of submitted information in print and electronic formats.</li>
                <li>No person or entity may copy, scrape, or republish Epic-Jobs content without fully executed consent from the CEO of AVM, Inc.</li>
              </ul>
              <Link to="/terms" target="_blank" rel="noreferrer">Read full Terms of Use</Link>
            </div>
          )}

          <div className="form-group">
            <button className="theme-btn btn-style-one" type="submit" name={isLogin ? 'log-in' : 'register'} disabled={isLoading}>
              {isLoading ? 'Loading...' : isLogin ? 'Log In' : 'Register'}
            </button>
          </div>
        </form>

        <div className="bottom-box">
          <div className="text">
            {isLogin ? (
              <>Don't have an account? <Link className="call-modal signup" to={authLink('signup')}>Signup</Link></>
            ) : (
              <>Have an account? <Link className="call-modal signup" to={authLink('login')}>Login</Link></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
